import React from "react";
import PlatformIcon, { platformLabel } from "./PlatformIcon";
import { SeverityPill, RiskBadge, timeAgo } from "./bits";
import { Heart, MessageCircle, Repeat2, ExternalLink, MapPin } from "lucide-react";

export default function PostCard({ post, onClick, onJumpToMap, compact = false }) {
  return (
    <article
      className="group border border-slate-200/60 bg-white hover:border-[#2563eb]/30 hover:shadow-[0_8px_30px_rgb(0,0,0,0.04)] transition-all duration-300 cursor-pointer rounded-sm overflow-hidden"
      onClick={() => onClick && onClick(post)}
      data-testid={`post-card-${post.id}`}
    >
      <header className="flex items-center gap-2 px-4 py-3 border-b border-slate-100">
        <PlatformIcon platform={post.platform} className="h-4 w-4" />
        <div className="min-w-0">
          <div className="text-[13px] font-semibold truncate">{post.author}</div>
          <div className="text-[10px] font-mono text-slate-400">{post.handle} · {timeAgo(post.created_at)}</div>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <SeverityPill severity={post.severity} />
        </div>
      </header>
      <div className="px-4 py-3">
        <p className="text-[13px] text-slate-700 leading-relaxed font-medium">
          {post.ai_summary || (post.content ? `${post.content.substring(0, 150)}...` : "Analyst summary pending.")}
        </p>
        <div className="mt-2">
          <a
            href={post.url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="text-[11px] text-[#2563eb] font-semibold hover:underline flex items-center gap-1"
          >
            Read Intelligence Source <ExternalLink className="h-2.5 w-2.5" />
          </a>
        </div>
        {!compact && post.image && (
          <img src={post.image} alt="" className="mt-3 w-full h-32 object-cover rounded-sm" />
        )}
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <span className="kicker text-slate-400">{post.venture_code}</span>
          <span className="text-slate-300">·</span>
          <span className="text-[11px] text-slate-500">{post.site}</span>
          <span className="text-slate-300">·</span>
          <span className="text-[11px] text-slate-500 truncate">{post.activity_category}</span>
        </div>
      </div>
      <footer className="flex items-center justify-between px-4 py-2 border-t border-slate-100 text-[11px] text-slate-500 font-mono">
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-1"><Heart className="h-3 w-3" /> {post.likes}</span>
          <span className="flex items-center gap-1"><Repeat2 className="h-3 w-3" /> {post.shares}</span>
          <span className="flex items-center gap-1"><MessageCircle className="h-3 w-3" /> {post.comments}</span>
        </div>
        <div className="flex items-center gap-2">
          <RiskBadge score={post.risk_score} />
          {onJumpToMap && post.location && (
            <button
              onClick={(e) => { e.stopPropagation(); onJumpToMap(post); }}
              title={`Zoom map to ${post.site}`}
              className="flex items-center gap-1 px-1.5 py-0.5 border border-slate-200 hover:border-[#2563eb] hover:text-[#2563eb] rounded-sm transition-colors"
              data-testid={`jump-to-map-${post.id}`}
            >
              <MapPin className="h-3 w-3" />
              <span className="text-[10px] uppercase tracking-[0.14em]">Map</span>
            </button>
          )}
          <ExternalLink className="h-3 w-3 opacity-0 group-hover:opacity-100" />
        </div>
      </footer>
    </article>
  );
}
